import React from "react";
import { useAppSelector } from "../../../redux/store";
import { useNavigate } from "react-router-dom";
import SideNavbar from "../../SideNavbar";

// Icons for providers
const ExotelIcon = () => (
  <img src="/images/desktop/exotel-logo.svg" alt="Exotel Logo" className="w-30 h-30" />
);
const VoxBayIcon = () => (
  <img src="/images/desktop/voxbay-logo.svg" alt="Vox Bay Logo" className="w-30 h-30" />
);

interface ProviderCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  status: "available" | "coming-soon";
  onClick?: () => void;
}

const ProviderCard: React.FC<ProviderCardProps> = ({ title, description, icon, status, onClick }) => {
  const isAvailable = status === "available";
  return (
    <div
      className={`
        relative p-6 rounded-xl shadow-md transition-all duration-300
        ${isAvailable 
          ? "bg-white border border-gray-200 hover:shadow-lg cursor-pointer" 
          : "bg-gray-100 border border-gray-200 cursor-not-allowed"}
      `}
      onClick={isAvailable ? onClick : undefined}
    >
      {!isAvailable && (
        <div className="absolute top-3 right-3 bg-amber-100 text-amber-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
          Coming Soon
        </div>
      )}
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 mt-1">{icon}</div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">{title}</h3>
          <p className="text-sm text-gray-600">{description}</p>
        </div>
      </div>
    </div>
  );
};

const CloudTelephonyIntegrations: React.FC = () => {
  const isCollapsed = useAppSelector((state) => state.sidebar.isCollapsed);
  const navigate = useNavigate();
  const handleGoBack = () => {
    navigate('/integrations');
};

  return (
    <div className="min-h-screen bg-gray-200">
      <div className="fixed top-16 left-0 h-full z-10">
          <SideNavbar />
      </div>
      <div className={`${isCollapsed ? 'ml-[70px]' : 'ml-[290px]'} mt-[109px] p-8 transition-all duration-300`}>
          <div className="bg-white p-8 rounded-3xl shadow-md">
              {/* Header */}
              <div className="flex items-center justify-between mb-8">
                  <div>
                      <button 
                          onClick={handleGoBack}
                          className="text-gray-600 hover:text-gray-900 flex items-center gap-2 mb-4"
                      >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                          Back to Integrations
                      </button>
                      <div>
                        <h1 className="text-2xl font-bold text-gray-900">Cloud Telephony Integrations</h1>
                        <p className="text-gray-600 mt-2">Connect your CRM to cloud telephony providers to enable advanced calling features</p>
                      </div>
                  </div>
                  <div className="flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30"> 
                      <path opacity="0.5" d="M15.5562 14.5477L15.1007 15.0272C15.1007 15.0272 14.0181 16.167 11.0631 13.0559C8.10812 9.94484 9.1907 8.80507 9.1907 8.80507L9.47752 8.50311C10.1841 7.75924 10.2507 6.56497 9.63424 5.6931L8.37326 3.90961C7.61028 2.8305 6.13596 2.68795 5.26145 3.60864L3.69185 5.26114C3.25823 5.71766 2.96765 6.30945 3.00289 6.96594C3.09304 8.64546 3.81071 12.259 7.81536 16.4752C12.0621 20.9462 16.0468 21.1239 17.6763 20.9631C18.1917 20.9122 18.6399 20.6343 19.0011 20.254L20.4217 18.7584C21.3806 17.7489 21.1102 16.0182 19.8833 15.312L17.9728 14.2123C17.1672 13.7486 16.1858 13.8848 15.5562 14.5477Z" fill="#00cc33"></path>
                    </svg>
                  </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <ProviderCard
                  title="Exotel"
                  description="Integrate Exotel for cloud-based calling, call tracking, and automation."
                  icon={<ExotelIcon />}
                  status="available"
                  onClick={() => navigate("/integrations/cloud-telephony/exotel")}
                />
                <ProviderCard
                  title="Vox Bay"
                  description="Enable Vox Bay integration for scalable cloud telephony and analytics."
                  icon={<VoxBayIcon />}
                  status="coming-soon"
                />
              </div>
          </div>
      </div>
    </div>
  );
};

export default CloudTelephonyIntegrations;
